import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';

import type { FilterSearchValues } from '~/components/SystemConfiguration/DataExport/GeneratedReports/GeneratedReportsFilters';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  GeneratedReportListResponse,
  GeneratedReportListResponseGeneratedReportResponse,
  GeneratedReportResponse,
  GenerateReportRequest,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

export type GeneratedReport = GeneratedReportListResponseGeneratedReportResponse;

export type SortableColumn = 'requested_at' | 'status';

export interface FilterParams {
  name: string;
  status: string[];
  requestedAtStart: string;
  requestedAtEnd: string;
  page: number;
  limit: number;
  sortBy: SortableColumn;
  orderBy: 'asc' | 'desc';
}

const DEFAULT_FILTER_PARAMS: FilterParams = {
  name: '',
  status: [],
  requestedAtStart: '',
  requestedAtEnd: '',
  page: 1,
  limit: 20,
  sortBy: 'requested_at',
  orderBy: 'desc',
};

export const useGeneratedReports = () => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const [filterParams, setFilterParams] = useState<FilterParams>(DEFAULT_FILTER_PARAMS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/reports/generated`;

  const queryString = useMemo(() => {
    const params = new URLSearchParams();

    if (filterParams.name) {
      params.append('name', filterParams.name);
    }
    filterParams.status.forEach((s) => {
      params.append('status[]', s);
    });
    if (filterParams.requestedAtStart) {
      params.append('requested_at_start', filterParams.requestedAtStart);
    }
    if (filterParams.requestedAtEnd) {
      params.append('requested_at_end', filterParams.requestedAtEnd);
    }
    params.append('page', String(filterParams.page));
    params.append('limit', String(filterParams.limit));
    params.append('sort_by', filterParams.sortBy);
    params.append('order_by', filterParams.orderBy);

    return params.toString();
  }, [filterParams]);

  const fetchUrl = `${baseRoute}?${queryString}`;

  const {
    isLoading,
    isError,
    data: reportsData,
    reloadData: reloadReports,
  } = useDataFetcher<GeneratedReportListResponse>(fetchUrl);

  const reports = reportsData?.reports || [];
  const total = reportsData?.total || 0;

  const updateFilterParams = useCallback((updates: Partial<FilterParams>) => {
    setFilterParams((prev) => ({
      ...prev,
      ...updates,
      page: updates.page ?? 1,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterParams(DEFAULT_FILTER_PARAMS);
  }, []);

  const deleteReport = async (reportId: number): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      await axios.delete(`${baseRoute}/${reportId}`);
      await reloadReports();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadReport = (reportId: number): void => {
    window.open(`${baseRoute}/${reportId}/download`, '_blank');
  };

  const generateReport = async (request: GenerateReportRequest): Promise<GeneratedReportResponse | null> => {
    try {
      setIsSubmitting(true);
      const { data } = await axios.post<GeneratedReportResponse>(baseRoute, request);
      await reloadReports();
      return data;
    } catch (error) {
      await reportAxiosError(error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const setPage = useCallback((page: number) => {
    setFilterParams((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setFilterParams((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const handleSearch = useCallback((searchValues: FilterSearchValues) => {
    setFilterParams((prev) => ({
      ...prev,
      ...searchValues,
      page: 1,
    }));
  }, []);

  const handleSortChange = useCallback((columnId: string, direction: 'asc' | 'desc') => {
    if (columnId === 'requested_at' || columnId === 'status') {
      setFilterParams((prev) => ({ ...prev, sortBy: columnId, orderBy: direction, page: 1 }));
    }
  }, []);

  return {
    reports,
    total,
    isLoading,
    isError,
    reloadReports,
    filterParams,
    updateFilterParams,
    clearFilters,
    handleSearch,
    handleSortChange,
    deleteReport,
    downloadReport,
    generateReport,
    isSubmitting,
    setPage,
    setLimit,
  };
};
